/**
 * Share Modal
 * Prompts user to tweet after winning Round 1 for a boost reward
 */

import React, { useState, useEffect } from 'react';
import { useShareBoost } from '../../hooks/useShareBoost';
import './Modal.css';
import './ShareModal.css';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  normalPayout?: number; // e.g., 3.6 (Round 2 base)
  boostedPayout?: number; // e.g., 5.4 (3.6 * 1.5)
  onBoostClaimed?: () => void; // Callback to refresh parent's boost status
}

type ModalState = 'initial' | 'verifying' | 'success';

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  normalPayout = 3.6,
  boostedPayout = 5.4,
  onBoostClaimed
}) => {
  const { 
    claimBoost, 
    getTweetText, 
    openTweetIntent,
    markShownThisSession,
    boostValue
  } = useShareBoost();
  
  const [modalState, setModalState] = useState<ModalState>('initial');
  const [verifyProgress, setVerifyProgress] = useState(0);
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setModalState('initial');
      setVerifyProgress(0);
    }
  }, [isOpen]);
  
  // Mark as shown when modal opens
  useEffect(() => {
    if (isOpen) {
      markShownThisSession();
    }
  }, [isOpen, markShownThisSession]);
  
  /**
   * Handle tweet button click
   * 1. Open Twitter intent immediately
   * 2. Call backend to claim boost
   * 3. Refresh parent's boost status
   * 4. Close modal
   */
  const handleTweetClick = async () => {
    // Open Twitter immediately
    openTweetIntent();
    
    // Claim the boost from backend
    await claimBoost();
    
    // Refresh parent's boost status so UI updates
    if (onBoostClaimed) {
      onBoostClaimed();
    }
    
    // Close modal
    onClose();
  };
  
  const handleMaybeLater = () => {
    onClose();
  };
  
  if (!isOpen) return null;
  
  const tweetPreview = getTweetText();
  
  return (
    <div className="modal-overlay share-modal-overlay" onClick={handleMaybeLater}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        {/* Scanline effect */}
        <div className="share-scanlines" />
        
        {/* Close button */}
        <button 
          className="share-modal-close" 
          onClick={handleMaybeLater}
          aria-label="Close modal"
        >
          ×
        </button>
        
        {modalState === 'initial' && (
          <>
            {/* Header with emoji + title */}
            <div className="share-modal-header">
              <span className="share-devil-emoji">😈</span>
              <h2 className="share-modal-title">Boost your next level</h2>
            </div>
            
            {/* Boost multiplier in highlighted container */}
            <div className="share-boost-container">
              <span className="boost-from">{normalPayout}x</span>
              <span className="boost-arrow">→</span>
              <span className="boost-to">{boostedPayout}x</span>
            </div>
            
            {/* Tweet preview box */}
            <div className="share-tweet-preview">
              <div className="share-tweet-content">
                {tweetPreview}
              </div>
            </div>
            
            {/* Tweet button */}
            <button 
              className="share-tweet-button"
              onClick={handleTweetClick}
            >
              <span className="button-glow" />
              Tweet to boost 🚀
            </button>
            
            {/* Maybe later */}
            <button 
              className="share-maybe-later"
              onClick={handleMaybeLater}
            >
              Maybe later
            </button>
          </>
        )}
        
        {modalState === 'verifying' && (
          <div className="share-verifying">
            <div className="share-verifying-icon">
              <div className="share-spinner" />
            </div>
            <h3 className="share-verifying-title">Verifying...</h3>
            <div className="share-progress-bar">
              <div 
                className="share-progress-fill" 
                style={{ width: `${verifyProgress}%` }}
              />
            </div>
            <p className="share-verifying-text">
              Checking your tweet
            </p>
          </div>
        )}
        
        {modalState === 'success' && (
          <div className="share-success">
            <div className="share-success-icon">🎉</div>
            <h3 className="share-success-title">Boost Unlocked!</h3>
            <p className="share-success-text">
              Your next Round 2 payout is now <span className="share-boost-value">{boostedPayout}x</span>
            </p>
            <div className="share-success-badge">
              <span className="badge-multiplier">{boostValue}x</span>
              <span className="badge-label">BOOST ACTIVE</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareModal;

